// Plain async API helpers for the server-side "send payment link" command
// (contract A/B — see /Users/admin/Desktop/2026-09-23-payment-link-delivery-waves.md).
//
// These are NOT Redux thunks — they are called directly (await sendPaymentLink(...))
// from components/thunks that need explicit control over the send, and are the single
// source of truth other implementers (settings, payments tab) should import from.
import { callApi } from '../../../utils/apiCall';
import extractApiErrorMessage from '../../../utils/extractApiError';

// crypto.randomUUID isn't available in every runtime (older Safari, some jsdom
// setups) — fall back to a collision-resistant enough id for an idempotency key.
export function generateIdempotencyKey() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch (e) {
        // fall through to the fallback below
    }
    return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const buildResult = (res) => {
    if (res?.ok) {
        return { ok: true, data: res.data, error: null, status: res.status };
    }
    return {
        ok: false,
        data: res?.data ?? null,
        error: extractApiErrorMessage(res, 'Something went wrong'),
        status: res?.status ?? 0,
    };
};

/**
 * POST /api/v1/practices/{p}/leads/{l}/treatments/{t}/payment-links/ (contract A)
 * @param {{practiceId: number|string, leadId: number|string, treatmentId: number|string, body: object}} args
 * @returns {Promise<{ok: boolean, data: object|null, error: string|null, status: number}>}
 */
export async function sendPaymentLink({ practiceId, leadId, treatmentId, body = {} }) {
    const payload = { idempotency_key: generateIdempotencyKey(), ...body };
    const res = await callApi(
        `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payment-links/`,
        'post',
        payload,
    );
    return buildResult(res);
}

/**
 * POST /api/v1/practices/{p}/leads/{l}/treatments/{t}/payments/{pay}/payment-links/resend/ (contract B)
 * @param {{practiceId: number|string, leadId: number|string, treatmentId: number|string, paymentId: number|string, body: object}} args
 * @returns {Promise<{ok: boolean, data: object|null, error: string|null, status: number}>}
 */
export async function resendPaymentLink({ practiceId, leadId, treatmentId, paymentId, body = {} }) {
    const res = await callApi(
        `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${paymentId}/payment-links/resend/`,
        'post',
        body,
    );
    return buildResult(res);
}

const CHANNEL_LABELS = { email: 'Email', whatsapp: 'WhatsApp' };
const FAILED_STATES = new Set(['blocked', 'failed']);
const SENT_VERBS = {
    queued: 'queued',
    accepted: 'accepted',
    sent: 'sent',
    delivered: 'delivered',
    read: 'read',
};

/**
 * Turns the command response's per-channel `channels` map into a short human
 * summary, e.g. "Email queued to jane@x.com; WhatsApp failed: not opted in".
 * @param {object} data - the command response (contract A/B success shape)
 * @param {{email?: string, phone?: string}} [recipients]
 */
export function summarisePaymentLinkResult(data, recipients = {}) {
    const channels = data?.channels;
    if (!channels || typeof channels !== 'object') return '';

    const parts = Object.entries(channels).reduce((acc, [channel, info]) => {
        if (!info || info.state === 'not_requested') return acc;
        const label = CHANNEL_LABELS[channel] || channel;
        if (FAILED_STATES.has(info.state)) {
            acc.push(`${label} failed: ${info.error || 'delivery blocked'}`);
            return acc;
        }
        const verb = SENT_VERBS[info.state] || info.state;
        const recipient = channel === 'email' ? recipients.email : recipients.phone;
        acc.push(recipient ? `${label} ${verb} to ${recipient}` : `${label} ${verb}`);
        return acc;
    }, []);

    return parts.join('; ');
}

/**
 * True when at least one requested channel is progressing (not failed/blocked/not_requested).
 * @param {object} data - the command response
 */
export function hasAnySuccessfulChannel(data) {
    const channels = data?.channels;
    if (!channels || typeof channels !== 'object') return false;
    return Object.values(channels).some(
        (info) => info && info.state !== 'not_requested' && !FAILED_STATES.has(info.state)
    );
}
